import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catInfo = categories.find((c) => c.slug === article.category);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://www.ky.app/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: catInfo?.name || article.category,
                    item: `https://www.ky.app/category/${article.category}`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                    item: `https://www.ky.app/article/${article.slug}`,
                  },
                ],
              },
              {
                '@type': 'Article',
                headline: article.title,
                description: article.excerpt,
                author: {
                  '@type': 'Person',
                  name: article.author,
                },
                datePublished: article.date,
                url: `https://www.ky.app/article/${article.slug}`,
                publisher: {
                  '@type': 'Organization',
                  name: 'KY App',
                },
              },
            ],
          }),
        }}
      />

      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto py-3">
              <nav className="flex items-center gap-1.5 text-xs text-foreground-400 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link
                  to={`/category/${article.category}`}
                  className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {catInfo?.name || article.category}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Article Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors mb-4"
              >
                {catInfo?.name || article.category}
              </Link>
              <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <article
                className="prose-custom text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles
                  .filter((a) => a.slug !== article.slug && a.category === article.category)
                  .slice(0, 3)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group p-5 rounded-2xl bg-background-50 hover:bg-background-200/50 transition-colors duration-200 cursor-pointer"
                    >
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                        {related.excerpt}
                      </p>
                      <div className="text-xs text-foreground-400 flex items-center gap-3">
                        <span>{related.date}</span>
                        <span>{related.readTime}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}